/**
 * Shared helper for atomic cart writes that keep empty cylinder
 * quantities in sync with gas/pant tagged products.
 *
 * A server-side checkout validation rejects every cart write that
 * leaves cylinder quantities out of sync. Because of that, each write
 * must carry the tagged products and their cylinder lines together in
 * one request. This helper is the single place that computes cylinder
 * targets and performs those writes.
 *
 * Config: window.cylinderConfig = { gasVariant, pantVariant } (theme.liquid).
 */
window.CylinderCart = (() => {
  function config() {
    const cfg = window.cylinderConfig;
    if (!cfg || !cfg.gasVariant || !cfg.pantVariant) return null;
    return cfg;
  }

  function parseTags(value) {
    return (value || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  // Read the whole cart (variant id, quantity, product tags) through the
  // Section Rendering API. Product tags are not available in /cart.js.
  async function fetchState() {
    const response = await fetch(`${window.routes.cart_url}?sections=cylinder-state`);
    if (!response.ok) throw new Error(`cylinder-state HTTP ${response.status}`);
    const sections = await response.json();
    const template = document.createElement('template');
    template.innerHTML = sections['cylinder-state'];
    const json = template.content.querySelector('[data-cylinder-state]');
    if (!json) throw new Error('cylinder-state section markup missing');
    return JSON.parse(json.textContent);
  }

  // Cylinder line items to append to a /cart/add.js call that adds
  // `quantity` units of a product with `tags`.
  function cylinderDeltaItems(quantity, tags) {
    const cfg = config();
    if (!cfg) return [];
    const items = [];
    if (tags.includes('gas')) items.push({ id: cfg.gasVariant, quantity });
    if (tags.includes('pant')) items.push({ id: cfg.pantVariant, quantity });
    return items;
  }

  // Compute cart-wide cylinder targets after applying `changes`
  // (variant id -> absolute quantity). `tagsByVariant` supplies tags for
  // variants that are not in the cart yet.
  function computeTargets(items, changes, tagsByVariant) {
    const cfg = config();
    let gasCylinderNow = 0;
    let pantCylinderNow = 0;
    const byId = new Map();
    items.forEach((item) => {
      if (item.id === cfg.gasVariant) gasCylinderNow += item.quantity;
      if (item.id === cfg.pantVariant) pantCylinderNow += item.quantity;
      const existing = byId.get(item.id);
      if (existing) existing.quantity += item.quantity;
      else byId.set(item.id, { id: item.id, quantity: item.quantity, tags: item.tags });
    });

    Object.entries(changes).forEach(([key, quantity]) => {
      const id = Number(key);
      const existing = byId.get(id);
      if (existing) existing.quantity = quantity;
      else byId.set(id, { id, quantity, tags: (tagsByVariant && tagsByVariant[id]) || [] });
    });

    let gasTagged = 0;
    let pantTagged = 0;
    byId.forEach((item) => {
      // Cylinder lines never count toward the tagged totals, even if the
      // cylinder products themselves carry the tags.
      if (item.id === cfg.gasVariant || item.id === cfg.pantVariant) return;
      if (item.quantity <= 0) return;
      if (item.tags.includes('gas')) gasTagged += item.quantity;
      if (item.tags.includes('pant')) pantTagged += item.quantity;
    });

    return { gasTagged, pantTagged, gasCylinderNow, pantCylinderNow };
  }

  /**
   * Apply `changes` (variant id -> absolute quantity) and the matching
   * cylinder quantities in one /cart/update.js request.
   *
   * Direct changes to the cylinder variants are ignored: their
   * quantities always follow the computed targets.
   *
   * options.tagsByVariant: tags for variants that are new to the cart.
   * options.body: extra body fields (sections, sections_url).
   * Returns the fetch Response.
   */
  async function atomicUpdate(changes = {}, options = {}) {
    const cfg = config();
    if (!cfg) throw new Error('cylinderConfig missing');

    const items = await fetchState();
    const targets = computeTargets(items, changes, options.tagsByVariant);

    const updates = {};
    Object.entries(changes).forEach(([key, quantity]) => {
      const id = Number(key);
      if (id === cfg.gasVariant || id === cfg.pantVariant) return;
      updates[id] = quantity;
    });
    // Write a cylinder key only when its quantity must change. An
    // untouched cylinder variant must stay out of the request: writing
    // a key for a variant the session cannot resolve fails the whole
    // update.
    if (targets.gasTagged !== targets.gasCylinderNow) {
      updates[cfg.gasVariant] = targets.gasTagged;
    }
    if (targets.pantTagged !== targets.pantCylinderNow) {
      updates[cfg.pantVariant] = targets.pantTagged;
    }
    if (Object.keys(updates).length === 0) {
      // Nothing to write (e.g. a direct edit of a cylinder line that the
      // computed targets reject). Return the current cart unchanged.
      return fetch(`${window.routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body: JSON.stringify({ updates: {}, ...(options.body || {}) }) },
      });
    }

    const body = JSON.stringify({ updates, ...(options.body || {}) });
    return fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
  }

  return { config, parseTags, fetchState, cylinderDeltaItems, atomicUpdate };
})();
